import React, { useEffect, useRef, useState } from 'react';
import { Loader, Text } from 'src/@vymo/ui/atoms';
import classNames from 'classnames';
import { getAppLocationInfo } from 'src/workspace/utils';
import { createIframeEventHandlers } from './queries';
import styles from './index.module.scss';

function LegacyWebInterface() {
  const { hash } = getAppLocationInfo();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Set flags in window.legacyWeb for iframe access
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const previousLegacyWeb = window.legacyWeb;
      window.legacyWeb = {
        isWeb3: true,
        hideHeader: true,
        hideSidebar: true,
        ...(previousLegacyWeb || {}),
      };

      return () => {
        if (previousLegacyWeb) {
          window.legacyWeb = previousLegacyWeb;
        } else {
          delete window.legacyWeb;
        }
      };
    }
    return undefined;
  }, []);

  /**
   * Handle iframe load event
   */
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return undefined;
    }

    const { handleLoad: handleLoadEvent, handleError: handleErrorEvent } =
      createIframeEventHandlers(
        () => {
          setLoading(false);
          setError(null);
        },
        () => {
          setLoading(false);
          setError('Failed to load the legacy web interface');
        },
      );

    iframe.addEventListener('load', handleLoadEvent);
    iframe.addEventListener('error', handleErrorEvent);

    return () => {
      iframe.removeEventListener('load', handleLoadEvent);
      iframe.removeEventListener('error', handleErrorEvent);
    };
  }, []);

  if (error) {
    return (
      <div className={classNames(styles.error)}>
        <Text type="h3">Error Loading Interface</Text>
        <Text>{error}</Text>
      </div>
    );
  }

  const baseUrl = `/#/${hash.split('/web2/')[1]}`;

  return (
    <div className={classNames(styles.container)}>
      {loading && (
        <div className={styles.loader}>
          <Loader />
        </div>
      )}
      <iframe
        title="Legacy Web Interface"
        ref={iframeRef}
        src={baseUrl}
        className={styles.iframe}
        allow="fullscreen"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
      />
    </div>
  );
}

export default React.memo(LegacyWebInterface);
